import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { getPdfDocument, loadPdfJsLib } from "services/pdfjs";

// Renders a PDF's pages to canvases stacked vertically on a WHITE background —
// unlike an <iframe>/<embed>, whose native viewer chrome is dark and not
// stylable. Pages flow at their natural height so the surrounding container
// owns scrolling (no nested scroll context). Large docs are capped inline (the
// modal shows everything).
//
// We fetch the source into an ArrayBuffer and render from the bytes (not the
// URL) so the render can't be broken by the caller revoking a blob: URL while
// pdf.js is still reading it (e.g. when the user switches templates quickly).
const PdfPages = ({ url, maxPages = 15 }) => {
  const wrapRef = useRef(null);
  const pagesRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const [truncated, setTruncated] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let task;
    let pdfDoc;
    const renderTasks = [];
    const pages = pagesRef.current;
    const wrap = wrapRef.current;

    const render = async () => {
      if (!url || !pages) return;
      setStatus("loading");
      setTruncated(0);
      pages.innerHTML = "";
      try {
        const pdfjsLib = await loadPdfJsLib();
        if (cancelled || !pdfjsLib) return;
        const buffer = await (await fetch(url)).arrayBuffer();
        if (cancelled) return;
        task = getPdfDocument(pdfjsLib, { data: buffer });
        const pdf = await task.promise;
        if (cancelled) return;
        pdfDoc = pdf;

        const count = Math.min(pdf.numPages, maxPages);
        const dpr = window.devicePixelRatio || 1;
        const targetWidth = Math.min(wrap?.clientWidth || 680, 900);

        for (let n = 1; n <= count; n += 1) {
          if (cancelled) return;
          const page = await pdf.getPage(n);
          if (cancelled) return;
          const base = page.getViewport({ scale: 1 });
          const scale = targetWidth / base.width;
          const viewport = page.getViewport({ scale: scale * dpr });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = `${targetWidth}px`;
          canvas.style.height = `${viewport.height / dpr}px`;
          canvas.style.display = "block";
          canvas.style.margin = "0 auto 12px";
          canvas.style.background = "#fff";
          canvas.style.boxShadow = "0 1px 5px rgba(0,0,0,0.22)";
          const renderTask = page.render({
            canvasContext: canvas.getContext("2d"),
            viewport,
          });
          renderTasks.push(renderTask);
          await renderTask.promise;
          if (cancelled) return;
          pages.appendChild(canvas);
        }
        if (!cancelled) {
          setTruncated(Math.max(0, pdf.numPages - count));
          setStatus("ready");
        }
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    };

    render();
    return () => {
      cancelled = true;
      renderTasks.forEach((t) => {
        try {
          t.cancel();
        } catch (e) {
          // ignore
        }
      });
      try {
        task?.destroy?.();
        pdfDoc?.destroy?.();
      } catch (e) {
        // ignore
      }
    };
  }, [url, maxPages]);

  return (
    <Box ref={wrapRef} sx={{ bgcolor: "#fff" }}>
      {status === "loading" && (
        <Stack
          alignItems={"center"}
          justifyContent={"center"}
          sx={{ minHeight: 160 }}
        >
          <CircularProgress size={28} />
        </Stack>
      )}
      {status === "error" && (
        <Stack
          alignItems={"center"}
          justifyContent={"center"}
          sx={{ minHeight: 160 }}
        >
          <Typography color={"text.secondary"}>
            {"Couldn't render the preview."}
          </Typography>
        </Stack>
      )}
      <Box
        ref={pagesRef}
        sx={{ display: status === "ready" ? "block" : "none" }}
      />
      {truncated > 0 && status === "ready" && (
        <Typography variant={"caption"} color={"text.secondary"}>
          {`Showing the first ${maxPages} pages — ${truncated} more in the full view.`}
        </Typography>
      )}
    </Box>
  );
};

export default PdfPages;
